import React from "react";
import styled from "styled-components";
import PageLayout from "lib/components/PageLayout";
import Head from "lib/components/Head";

// Expert images
import ExpertLoriBruun from "lib/assets/expert-lori-bruun.png";
import ExpertMichaelValverde from "lib/assets/expert-michael-valverde.png";
import ExpertJonathanMicale from "lib/assets/expert-jonathan-micale.png";
import ExpertEfrenHernandez from "lib/assets/expert-efren-hernandez.png";

// Feature icons
import IconGauge from "lib/assets/icon-gauge.png";

// Background
import IntelligenceBg from "lib/assets/intelligence-bg.png";

// ============================================
// Styled Components
// ============================================

const PageWrapper = styled.div<{ bgImage: string }>`
  background: url(${(props) => props.bgImage});
  background-position: 0px -20px;
  background-size: cover;
  background-repeat: no-repeat;
  background-color: #000000;
  min-height: 100vh;
  overflow-x: hidden;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 70px 80px 0;
  min-height: 750px;
  text-align: center;
  max-width: 1440px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 40px 24px;
    min-height: auto;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1280px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  font-weight: 700;
  line-height: 1.38;
  margin: 103px 0 0;
  background: linear-gradient(90deg, #ffffff 0%, #bef2ff 52.4%, #ff72d9 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 1024px) {
    font-size: 80px;
    margin-top: 60px;
  }

  @media (max-width: 768px) {
    font-size: 48px;
    margin-top: 40px;
  }
`;

const HeroTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  max-width: 830px;
  margin-top: 0;
`;

const HeroSubtitle = styled.p`
  font-size: 20px;
  line-height: 1.4;
  color: #e2e2e2;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  color: #0f172b;
  font-size: 18px;
  font-weight: 600;
  width: 335px;
  height: 60px;
  border-radius: 9999px;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 16px;
    width: 100%;
    max-width: 335px;
    height: 52px;
  }
`;

const PricingText = styled.p`
  font-size: 20px;
  color: #e2e2e2;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TrustBadges = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding-top: 60px;
  flex-wrap: wrap;
  max-width: 500px;

  @media (max-width: 768px) {
    gap: 16px;
    padding-top: 32px;
  }
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #9ca3af;
  font-size: 14px;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

// Experts Section
const ExpertsSection = styled.section`
  padding: 96px 80px;
  max-width: 1440px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 60px 24px;
  }
`;

const ExpertsSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 64px;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 768px) {
    gap: 40px;
    padding: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 20px;
  color: #9ca3af;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ExpertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  max-width: 1280px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ExpertCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ExpertImageContainer = styled.div`
  position: relative;
  width: 128px;
  height: 128px;
  margin-bottom: 16px;
`;

const ExpertImageGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #a855f7 0%, #3b82f6 100%);
  border-radius: 50%;
  filter: blur(16px);
  opacity: 0.5;
`;

const ExpertImage = styled.div`
  position: relative;
  width: 128px;
  height: 128px;
  border-radius: 50%;
  border: 4px solid #5fff7a;
  background: #1f2937;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const VerifiedBadge = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 34px;
  height: 34px;
  background: #000000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #5fff7a;
  }
`;

const ExpertName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 8px;
`;

const ExpertTitle = styled.p`
  font-size: 14px;
  color: #5fff7a;
  margin: 0;
  line-height: 1.4;
`;

// Features Section
const FeaturesSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 80px;
  padding: 0 0 80px;
  max-width: 1440px;
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: 60px;
    padding: 0 0 60px;
  }
`;

const FeaturesSubSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 62px;

  @media (max-width: 768px) {
    gap: 40px;
  }
`;

const FeaturesHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  text-align: center;
  padding: 0 80px;

  @media (max-width: 768px) {
    padding: 0 24px;
    gap: 24px;
  }
`;

const FeaturesTitle = styled.h2`
  font-size: 48px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  line-height: 1.25;
  max-width: 900px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const FeaturesSubtitle = styled.p`
  font-size: 20px;
  color: #afbcd0;
  margin: 0;
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  padding: 0 104px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 0 40px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 24px;
  }
`;

const EnablesGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 19px;
  padding: 0 80px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    padding: 0 40px;
  }

  @media (max-width: 768px) {
    padding: 0 24px;
  }
`;

const FeatureCard = styled.div`
  background: rgba(5, 8, 36, 0.9);
  border: 1px solid rgba(115, 85, 247, 0.2);
  border-radius: 16px;
  padding: 33px;
  backdrop-filter: blur(10px);
  min-height: 348px;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 24px;
  }
`;

const EnablesCard = styled.div`
  background: rgba(5, 8, 36, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 33px;
  max-width: 473px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const FeatureCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FeatureIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #ffffff;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 21px rgba(165, 180, 252, 0.5);

  svg {
    width: 32px;
    height: 32px;
    color: #2563eb;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
`;

const FeatureDescription = styled.p`
  font-size: 16px;
  line-height: 1.75;
  color: #cfd8ff;
  margin: 0;
`;

// Data Visualization Section
const DataVisualizationSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 64px;
  padding: 0 0 64px;
  max-width: 1440px;
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: 40px;
    padding: 0 0 40px;
  }
`;

const DataVizHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  padding: 0 80px;

  @media (max-width: 768px) {
    padding: 0 24px;
  }
`;

const DataVizTitle = styled.h2`
  font-size: 40px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const DataVizSubtitle = styled.p`
  font-size: 20px;
  color: #90a1b9;
  margin: 0;
  max-width: 672px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ChartRow = styled.div`
  display: flex;
  gap: 25px;
  padding: 0 95px;

  @media (max-width: 1200px) {
    flex-direction: column;
    padding: 0 40px;
  }

  @media (max-width: 768px) {
    padding: 0 24px;
  }
`;

const ChartPanel = styled.div`
  flex: 1;
  max-width: 849px;
  background: #0b0b0c;
  border: 1px solid #1d293d;
  border-radius: 24px;
  padding: 33px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    padding: 20px;
    gap: 20px;
  }
`;

const ChartPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChartTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChartIconWrapper = styled.div<{ color: string }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    stroke: ${(props) => props.color};
  }
`;

const ChartTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const LiveDataBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 13px;
  background: rgba(43, 127, 255, 0.1);
  border: 1px solid rgba(43, 127, 255, 0.2);
  border-radius: 9999px;
`;

const LiveDataText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #51a2ff;
`;

const ChartArea = styled.div`
  position: relative;
  width: 100%;
  height: 300px;

  @media (max-width: 768px) {
    height: 200px;
  }
`;

const LegendPanel = styled.div`
  width: 376px;
  min-width: 300px;
  background: rgba(15, 23, 43, 0.5);
  border: 1px solid #1d293d;
  border-radius: 24px;
  padding: 25px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 1200px) {
    width: 100%;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendDot = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  background: ${(props) => props.color};
  border-radius: 4px;
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const LegendNote = styled.p`
  font-size: 16px;
  color: #ffffff;
  margin: 0;
  opacity: 0.8;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

// CTA Section
const CTASection = styled.section`
  padding: 96px 80px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 24px;
  }
`;

const CTAContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 40px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 24px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const CTADescription = styled.p`
  font-size: 18px;
  color: #9ca3af;
  margin: 0 0 32px;
  line-height: 1.6;
`;

// ============================================
// Component
// ============================================

const EXPERTS = [
  {
    name: "Lori Bruun",
    title: "former Policy Analyst at USCIS",
    image: ExpertLoriBruun,
    linkedin: "https://www.linkedin.com/in/lori-bruun-b61854360",
  },
  {
    name: "Michael Valverde",
    title: "former Associate Director of Field Operations at USCIS",
    image: ExpertMichaelValverde,
    linkedin: "https://www.linkedin.com/in/michael-valverde-85780a356",
  },
  {
    name: "Jonathan Micale",
    title: "former USCIS Operational Executive",
    image: ExpertJonathanMicale,
    linkedin: "https://www.linkedin.com/in/jonathanmicale",
  },
  {
    name: "Efren Hernandez III",
    title: "former Supervisory Policy Analyst at USCIS",
    image: ExpertEfrenHernandez,
    linkedin: "https://www.linkedin.com/in/efren-hernandez-iii",
  },
];

// "What's included" features (top row - 3 cards)
const INCLUDED_FEATURES = [
  {
    title: "Monthly Reports",
    description:
      "Real-time approval rates, RFE rates, and processing-time trends across 23 employment-based case types, with expert analysis.",
    icon: "document",
  },
  {
    title: "Subscriber-Only Strategy Sessions",
    description:
      "Monthly consulting sessions with former USCIS officials and senior immigration policy experts.",
    icon: "users",
  },
  {
    title: "Ongoing Advisory Access",
    description:
      "Complimentary advisory calls with Lawfully’s Head of Insights & Strategy to help interpret trends and apply them to filing, case mix, and client strategy.",
    icon: "message",
  },
];

// "What it enables" features (bottom row - 2 cards)
const ENABLES_FEATURES = [
  {
    title: "Set expectations with confidence",
    description:
      "Comprehensive breakdowns of USCIS adjudication trends help you set accurate timelines and manage client expectations with real data.",
    icon: "gauge",
  },
  {
    title: "Make smarter filing decisions",
    description:
      "Adapt filing and sponsorship strategy based on real-time approval, RFE, and processing patterns.",
    icon: "file",
  },
];

// Trust badge icons - from Figma exports
const TrustBadgeIcon1 = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 14C10.8634 14 14 10.8634 14 7C14 3.13659 10.8634 0 7 0C3.13659 0 0 3.13659 0 7C0 10.8634 3.13659 14 7 14ZM10.0898 5.71484L6.58984 9.21484C6.33281 9.47188 5.91719 9.47188 5.66289 9.21484L3.91289 7.46484C3.65586 7.20781 3.65586 6.79219 3.91289 6.53789C4.16992 6.28086 4.58555 6.28086 4.83984 6.53789L6.125 7.82305L9.16016 4.78516C9.41719 4.52812 9.83281 4.52812 10.0871 4.78516C10.3441 5.04219 10.3441 5.45781 10.0871 5.71211L10.0898 5.71484Z"
      fill="#4ADE80"
    />
  </svg>
);

const TrustBadgeIcon2 = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.00001 0C7.12579 0 7.25157 0.0273437 7.36642 0.0792969L12.5152 2.26406C13.1168 2.51836 13.5652 3.11172 13.5625 3.82812C13.5488 6.54063 12.4332 11.5035 7.72189 13.7594C7.26525 13.9781 6.73478 13.9781 6.27814 13.7594C1.56681 11.5035 0.451184 6.54063 0.437512 3.82812C0.434778 3.11172 0.883216 2.51836 1.48478 2.26406L6.63634 0.0792969C6.74845 0.0273437 6.87423 0 7.00001 0ZM7.00001 1.82656V12.1625C10.7734 10.3359 11.7879 6.2918 11.8125 3.86641L7.00001 1.82656Z"
      fill="#60A5FA"
    />
  </svg>
);

const TrustBadgeIcon3 = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.75 1.75C1.75 1.26602 1.35898 0.875 0.875 0.875C0.391016 0.875 0 1.26602 0 1.75V10.9375C0 12.1461 0.978906 13.125 2.1875 13.125H13.125C13.609 13.125 14 12.734 14 12.25C14 11.766 13.609 11.375 13.125 11.375H2.1875C1.94687 11.375 1.75 11.1781 1.75 10.9375V1.75ZM12.868 4.11797C13.2098 3.77617 13.2098 3.22109 12.868 2.8793C12.5262 2.5375 11.9711 2.5375 11.6293 2.8793L8.75 5.76133L7.18047 4.1918C6.83867 3.85 6.28359 3.85 5.9418 4.1918L2.8793 7.2543C2.5375 7.59609 2.5375 8.15117 2.8793 8.49297C3.22109 8.83477 3.77617 8.83477 4.11797 8.49297L6.5625 6.05117L8.13203 7.6207C8.47383 7.9625 9.02891 7.9625 9.3707 7.6207L12.8707 4.1207L12.868 4.11797Z"
      fill="#C084FC"
    />
  </svg>
);

const VerifiedCheckIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

// Gauge/Speedometer icon for "Set expectations with confidence"
const GaugeIcon = () => (
  <img
    src={IconGauge}
    alt="Gauge"
    style={{ width: "auto", height: 24, objectFit: "contain" }}
  />
);

// Users icon from Figma - for Subscriber-Only Strategy Session
const UsersIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21.3334 28V25.3334C21.3334 23.9189 20.7715 22.5624 19.7713 21.5622C18.7711 20.562 17.4146 20.0001 16.0001 20.0001H8.00008C6.58559 20.0001 5.22904 20.562 4.22885 21.5622C3.22865 22.5624 2.66675 23.9189 2.66675 25.3334V28"
      stroke="#2563EB"
      strokeWidth="2.695"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 14.6667C14.9455 14.6667 17.3333 12.2789 17.3333 9.33337C17.3333 6.38785 14.9455 4.00004 12 4.00004C9.05452 4.00004 6.66671 6.38785 6.66671 9.33337C6.66671 12.2789 9.05452 14.6667 12 14.6667Z"
      stroke="#2563EB"
      strokeWidth="2.695"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M29.3334 28V25.3334C29.3325 24.1391 28.9352 22.979 28.2035 22.0351C27.4718 21.0913 26.4473 20.4171 25.291 20.1187"
      stroke="#2563EB"
      strokeWidth="2.695"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21.3334 4.14868C22.4928 4.44555 23.5204 5.11987 24.2542 6.06526C24.9881 7.01066 25.3864 8.17347 25.3864 9.37027C25.3864 10.5671 24.9881 11.7299 24.2542 12.6753C23.5204 13.6207 22.4928 14.295 21.3334 14.5919"
      stroke="#2563EB"
      strokeWidth="2.695"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="16" cy="16" r="12" />
    <path d="M16 8v8l4 4" />
  </svg>
);

// Document icon from Figma - for Data Trends
const DocumentIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21.3334 5.33337H9.33337C8.62613 5.33337 7.94785 5.61433 7.44776 6.11442C6.94766 6.61452 6.6667 7.2928 6.6667 8.00004V29.3334C6.6667 30.0406 6.94766 30.7189 7.44776 31.219C7.94785 31.7191 8.62613 32 9.33337 32H25.3334C26.0406 32 26.7189 31.7191 27.219 31.219C27.7191 30.7189 28 30.0406 28 29.3334V12L21.3334 5.33337Z"
      stroke="#2563EB"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20 5.33337V10.6667C20 11.374 20.281 12.0522 20.781 12.5523C21.2811 13.0524 21.9594 13.3334 22.6667 13.3334H28"
      stroke="#2563EB"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.6667 14.6667H12"
      stroke="#2563EB"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22.6667 20H12"
      stroke="#2563EB"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22.6667 25.3334H12"
      stroke="#2563EB"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Message/Headphones icon from Figma - for Ongoing Advisory Access
const MessageIcon = () => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 30 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15 2.8125C8.26758 2.8125 2.8125 8.26758 2.8125 15V17.3438C2.8125 18.123 2.18555 18.75 1.40625 18.75C0.626953 18.75 0 18.123 0 17.3438V15C0 6.71484 6.71484 0 15 0C23.2852 0 30 6.71484 30 15V23.4434C30 26.291 27.6914 28.5996 24.8379 28.5996L18.375 28.5938C17.8887 29.4316 16.9805 30 15.9375 30H14.0625C12.5098 30 11.25 28.7402 11.25 27.1875C11.25 25.6348 12.5098 24.375 14.0625 24.375H15.9375C16.9805 24.375 17.8887 24.9434 18.375 25.7812L24.8438 25.7871C26.1387 25.7871 27.1875 24.7383 27.1875 23.4434V15C27.1875 8.26758 21.7324 2.8125 15 2.8125ZM8.4375 12.1875H9.375C10.4121 12.1875 11.25 13.0254 11.25 14.0625V20.625C11.25 21.6621 10.4121 22.5 9.375 22.5H8.4375C6.36914 22.5 4.6875 20.8184 4.6875 18.75V15.9375C4.6875 13.8691 6.36914 12.1875 8.4375 12.1875ZM21.5625 12.1875C23.6309 12.1875 25.3125 13.8691 25.3125 15.9375V18.75C25.3125 20.8184 23.6309 22.5 21.5625 22.5H20.625C19.5879 22.5 18.75 21.6621 18.75 20.625V14.0625C18.75 13.0254 19.5879 12.1875 20.625 12.1875H21.5625Z"
      fill="#2563EB"
    />
  </svg>
);

// File with arrow icon from Figma - for Make smarter filing decisions
const FileIcon = () => (
  <svg
    width="25"
    height="28"
    viewBox="0 0 25 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.75 26.25H21.25C21.913 26.25 22.5489 25.9866 23.0178 25.5178C23.4866 25.0489 23.75 24.413 23.75 23.75V8.125L16.875 1.25H6.25C5.58696 1.25 4.95107 1.51339 4.48223 1.98223C4.01339 2.45107 3.75 3.08696 3.75 3.75V8.75M16.25 1.25V8.75H23.75M1.25 17.5H13.75M13.75 17.5L10 21.25M13.75 17.5L10 13.75"
      stroke="#2563EB"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const getFeatureIcon = (iconName: string) => {
  switch (iconName) {
    case "gauge":
      return <GaugeIcon />;
    case "users":
      return <UsersIcon />;
    case "clock":
      return <ClockIcon />;
    case "document":
      return <DocumentIcon />;
    case "message":
      return <MessageIcon />;
    case "file":
      return <FileIcon />;
    default:
      return <GaugeIcon />;
  }
};

// Chart trend icon (line chart icon)
const ChartTrendIcon = ({ color = "#bef264" }: { color?: string }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.667 14.167L8.333 7.5L11.667 10.833L18.333 4.167"
      stroke={color}
      strokeWidth="1.667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.333 4.167H18.333V9.167"
      stroke={color}
      strokeWidth="1.667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// H-1B Approval Rates Chart SVG
const ApprovalRatesChart = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 783 300"
    preserveAspectRatio="xMidYMid meet"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Grid lines */}
    <line x1="65" y1="265" x2="770" y2="265" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="200" x2="770" y2="200" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="135" x2="770" y2="135" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="70" x2="770" y2="70" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="5" x2="770" y2="5" stroke="#1e293b" strokeWidth="1" />

    {/* Y-axis labels */}
    <text
      x="20"
      y="270"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      90%
    </text>
    <text
      x="20"
      y="205"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      93%
    </text>
    <text
      x="20"
      y="140"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      95%
    </text>
    <text
      x="20"
      y="75"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      98%
    </text>
    <text
      x="12"
      y="15"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      100%
    </text>

    {/* Green gradient area (Lawfully) */}
    <defs>
      <linearGradient id="greenGradient" x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor="#bef264" stopOpacity="0.1" />
        <stop offset="95%" stopColor="#3b82f6" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="redGradient" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ff0000" stopOpacity="0.22" />
        <stop offset="100%" stopColor="#000000" stopOpacity="0" />
      </linearGradient>
    </defs>

    {/* Green area fill */}
    <path
      d="M87 30 L143 58 L197 80 L253 58 L308 30 L364 58 L418 30 L473 30 L528 58 L584 82 L639 30 L694 30 L755 35 L755 265 L87 265 Z"
      fill="url(#greenGradient)"
    />

    {/* Red area fill (partial - first half) */}
    <path
      d="M87 46 L143 72 L197 86 L253 68 L309 59 L365 51 L420 47 L420 265 L87 265 Z"
      fill="url(#redGradient)"
    />

    {/* Green line (Lawfully) */}
    <path
      d="M87 30 L143 58 L197 80 L253 58 L308 30 L364 58 L418 30 L473 30 L528 58 L584 82 L639 30 L694 30 L755 35"
      stroke="#bef264"
      strokeWidth="1.5"
      fill="none"
    />

    {/* Red line (USCIS) - only first half */}
    <path
      d="M87 46 L143 72 L197 86 L253 68 L309 59 L365 51 L420 47"
      stroke="#dc2626"
      strokeWidth="1.5"
      fill="none"
    />

    {/* Green data points */}
    <circle cx="87" cy="30" r="4.5" fill="#bef264" />
    <circle cx="143" cy="58" r="4.5" fill="#bef264" />
    <circle cx="197" cy="80" r="4.5" fill="#bef264" />
    <circle cx="253" cy="58" r="4.5" fill="#bef264" />
    <circle cx="308" cy="30" r="4.5" fill="#bef264" />
    <circle cx="364" cy="58" r="4.5" fill="#bef264" />
    <circle cx="418" cy="30" r="4.5" fill="#bef264" />
    <circle cx="473" cy="30" r="4.5" fill="#bef264" />
    <circle cx="528" cy="58" r="4.5" fill="#bef264" />
    <circle cx="584" cy="82" r="4.5" fill="#bef264" />
    <circle cx="639" cy="30" r="4.5" fill="#bef264" />
    <circle cx="694" cy="30" r="4.5" fill="#bef264" />
    <circle cx="755" cy="35" r="4.5" fill="#bef264" />

    {/* Red data points */}
    <circle cx="87" cy="46" r="4.5" fill="#dc2626" />
    <circle cx="143" cy="72" r="4.5" fill="#dc2626" />
    <circle cx="197" cy="86" r="4.5" fill="#dc2626" />
    <circle cx="253" cy="68" r="4.5" fill="#dc2626" />
    <circle cx="309" cy="59" r="4.5" fill="#dc2626" />
    <circle cx="365" cy="51" r="4.5" fill="#dc2626" />
    <circle cx="420" cy="47" r="4.5" fill="#dc2626" />

    {/* End labels */}
    <text
      x="738"
      y="15"
      fill="#ffffff"
      fontSize="20"
      fontWeight="700"
      fontFamily="sans-serif"
    >
      99%
    </text>
    <text
      x="428"
      y="50"
      fill="#ffffff"
      fontSize="20"
      fontWeight="700"
      fontFamily="sans-serif"
    >
      99%
    </text>

    {/* X-axis labels */}
    <text
      x="70"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Dec-24
    </text>
    <text
      x="125"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Jan-25
    </text>
    <text
      x="180"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Feb-25
    </text>
    <text
      x="235"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Mar-25
    </text>
    <text
      x="292"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Apr-25
    </text>
    <text
      x="345"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      May-25
    </text>
    <text
      x="403"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Jun-25
    </text>
    <text
      x="460"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Jul-25
    </text>
    <text
      x="512"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Aug-25
    </text>
    <text
      x="567"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Sep-25
    </text>
    <text
      x="623"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Oct-25
    </text>
    <text
      x="677"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Nov-25
    </text>
    <text
      x="733"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Dec-25
    </text>
  </svg>
);

// H-1B RFE Rates Chart SVG
const RFERatesChart = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 783 300"
    preserveAspectRatio="xMidYMid meet"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Grid lines */}
    <line x1="65" y1="265" x2="770" y2="265" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="190" x2="770" y2="190" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="111" x2="770" y2="111" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="33" x2="770" y2="33" stroke="#1e293b" strokeWidth="1" />

    {/* Y-axis labels */}
    <text
      x="30"
      y="270"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      0%
    </text>
    <text
      x="25"
      y="195"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      10%
    </text>
    <text
      x="25"
      y="116"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      20%
    </text>
    <text
      x="25"
      y="38"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      30%
    </text>

    {/* Purple gradient area (Regular) */}
    <defs>
      <linearGradient id="purpleGradient" x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor="#a855f7" stopOpacity="0.3" />
        <stop offset="95%" stopColor="#a855f7" stopOpacity="0" />
      </linearGradient>
    </defs>

    <path
      d="M87 150 L143 178 L197 190 L253 186 L308 155 L364 86 L418 10 L473 22 L528 118 L584 132 L639 123 L694 131 L755 123 L755 265 L87 265 Z"
      fill="url(#purpleGradient)"
    />

    {/* Purple line (Regular RFE) */}
    <path
      d="M87 150 L143 178 L197 190 L253 186 L308 155 L364 86 L418 10 L473 22 L528 118 L584 132 L639 123 L694 131 L755 123"
      stroke="#d946ef"
      strokeWidth="1.5"
      fill="none"
    />

    {/* Blue line (Premium RFE) - near bottom */}
    <path
      d="M87 261 L143 257 L197 252 L253 260 L308 252 L364 258 L418 263 L473 258 L528 256 L584 252 L639 259 L694 253 L755 260"
      stroke="#2563eb"
      strokeWidth="2"
      fill="none"
    />

    {/* Purple data points */}
    <circle cx="87" cy="150" r="4.5" fill="#d946ef" />
    <circle cx="143" cy="178" r="4.5" fill="#d946ef" />
    <circle cx="197" cy="190" r="4.5" fill="#d946ef" />
    <circle cx="253" cy="186" r="4.5" fill="#d946ef" />
    <circle cx="308" cy="155" r="4.5" fill="#d946ef" />
    <circle cx="364" cy="86" r="4.5" fill="#d946ef" />
    <circle cx="418" cy="10" r="4.5" fill="#d946ef" />
    <circle cx="473" cy="22" r="4.5" fill="#d946ef" />
    <circle cx="528" cy="118" r="4.5" fill="#d946ef" />
    <circle cx="584" cy="132" r="4.5" fill="#d946ef" />
    <circle cx="639" cy="123" r="4.5" fill="#d946ef" />
    <circle cx="694" cy="131" r="4.5" fill="#d946ef" />
    <circle cx="755" cy="123" r="4.5" fill="#d946ef" />

    {/* Blue data points */}
    <circle cx="87" cy="261" r="4.5" fill="#2563eb" />
    <circle cx="143" cy="257" r="4.5" fill="#2563eb" />
    <circle cx="197" cy="252" r="4.5" fill="#2563eb" />
    <circle cx="253" cy="260" r="4.5" fill="#2563eb" />
    <circle cx="308" cy="252" r="4.5" fill="#2563eb" />
    <circle cx="364" cy="258" r="4.5" fill="#2563eb" />
    <circle cx="418" cy="263" r="4.5" fill="#2563eb" />
    <circle cx="473" cy="258" r="4.5" fill="#2563eb" />
    <circle cx="528" cy="256" r="4.5" fill="#2563eb" />
    <circle cx="584" cy="252" r="4.5" fill="#2563eb" />
    <circle cx="639" cy="259" r="4.5" fill="#2563eb" />
    <circle cx="694" cy="253" r="4.5" fill="#2563eb" />
    <circle cx="755" cy="260" r="4.5" fill="#2563eb" />

    {/* End labels */}
    <text
      x="740"
      y="100"
      fill="#ffffff"
      fontSize="20"
      fontWeight="700"
      fontFamily="sans-serif"
    >
      17%
    </text>
    <text
      x="740"
      y="242"
      fill="#ffffff"
      fontSize="20"
      fontWeight="700"
      fontFamily="sans-serif"
    >
      &lt;1%
    </text>

    {/* X-axis labels */}
    <text
      x="70"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Dec-24
    </text>
    <text
      x="125"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Jan-25
    </text>
    <text
      x="180"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Feb-25
    </text>
    <text
      x="235"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Mar-25
    </text>
    <text
      x="292"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Apr-25
    </text>
    <text
      x="345"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      May-25
    </text>
    <text
      x="403"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Jun-25
    </text>
    <text
      x="460"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Jul-25
    </text>
    <text
      x="512"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Aug-25
    </text>
    <text
      x="567"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Sep-25
    </text>
    <text
      x="623"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Oct-25
    </text>
    <text
      x="677"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Nov-25
    </text>
    <text
      x="733"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Dec-25
    </text>
  </svg>
);

// H-1B Processing Times Chart SVG
const ProcessingTimesChart = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 783 300"
    preserveAspectRatio="xMidYMid meet"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Grid lines */}
    <line x1="65" y1="265" x2="770" y2="265" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="200" x2="770" y2="200" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="135" x2="770" y2="135" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="70" x2="770" y2="70" stroke="#1e293b" strokeWidth="1" />
    <line x1="65" y1="5" x2="770" y2="5" stroke="#1e293b" strokeWidth="1" />

    {/* Y-axis labels */}
    <text
      x="30"
      y="270"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      0.0
    </text>
    <text
      x="30"
      y="205"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      2.0
    </text>
    <text
      x="30"
      y="140"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      4.0
    </text>
    <text
      x="30"
      y="75"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      6.0
    </text>
    <text
      x="30"
      y="15"
      fill="#ffffff"
      fontSize="14"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      8.0
    </text>

    {/* Purple gradient area */}
    <defs>
      <linearGradient id="processingGradient" x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor="#a03cff" stopOpacity="0.3" />
        <stop offset="95%" stopColor="#a03cff" stopOpacity="0" />
      </linearGradient>
    </defs>

    <path
      d="M87 105 L143 80 L197 55 L253 72 L308 115 L364 148 L418 158 L473 168 L528 148 L584 140 L639 125 L694 88 L755 72 L755 265 L87 265 Z"
      fill="url(#processingGradient)"
    />

    {/* Purple line */}
    <path
      d="M87 105 L143 80 L197 55 L253 72 L308 115 L364 148 L418 158 L473 168 L528 148 L584 140 L639 125 L694 88 L755 72"
      stroke="#a03cff"
      strokeWidth="2"
      fill="none"
    />

    {/* Data points */}
    <circle cx="87" cy="105" r="4.5" fill="#a03cff" />
    <circle cx="143" cy="80" r="4.5" fill="#a03cff" />
    <circle cx="197" cy="55" r="4.5" fill="#a03cff" />
    <circle cx="253" cy="72" r="4.5" fill="#a03cff" />
    <circle cx="308" cy="115" r="4.5" fill="#a03cff" />
    <circle cx="364" cy="148" r="4.5" fill="#a03cff" />
    <circle cx="418" cy="158" r="4.5" fill="#a03cff" />
    <circle cx="473" cy="168" r="4.5" fill="#a03cff" />
    <circle cx="528" cy="148" r="4.5" fill="#a03cff" />
    <circle cx="584" cy="140" r="4.5" fill="#a03cff" />
    <circle cx="639" cy="125" r="4.5" fill="#a03cff" />
    <circle cx="694" cy="88" r="4.5" fill="#a03cff" />
    <circle cx="755" cy="72" r="4.5" fill="#a03cff" />

    {/* End label */}
    <text
      x="680"
      y="50"
      fill="#ffffff"
      fontSize="20"
      fontWeight="700"
      fontFamily="sans-serif"
    >
      6.0 months
    </text>

    {/* X-axis labels */}
    <text
      x="70"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Dec-24
    </text>
    <text
      x="125"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Jan-25
    </text>
    <text
      x="180"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Feb-25
    </text>
    <text
      x="235"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Mar-25
    </text>
    <text
      x="292"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Apr-25
    </text>
    <text
      x="345"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      May-25
    </text>
    <text
      x="403"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Jun-25
    </text>
    <text
      x="460"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Jul-25
    </text>
    <text
      x="512"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Aug-25
    </text>
    <text
      x="567"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Sep-25
    </text>
    <text
      x="623"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Oct-25
    </text>
    <text
      x="677"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Nov-25
    </text>
    <text
      x="733"
      y="285"
      fill="#ffffff"
      fontSize="13"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      Dec-25
    </text>
  </svg>
);

const IntelligencePage: React.FC = () => {
  const intelligencePricingUrl = process.env.NEXT_PUBLIC_BASE_HOST?.includes(
    "dev",
  )
    ? "https://pro-test.lawfully.com/pricing?product=intelligence"
    : "https://pro.lawfully.com/pricing?product=intelligence";

  return (
    <PageLayout>
      <Head
        title="Lawfully Intelligence | Real-time USCIS Processing Intelligence"
        desc="Real-time USCIS approval, RFE, and processing time intelligence for H-1B, L-1, O-1, EB-2 NIW, EB-1A, and 18+ additional case types. 3-6 months ahead of USCIS."
      />
      <PageWrapper bgImage={IntelligenceBg}>
        <ContentWrapper>
          {/* Hero Section */}
          <HeroSection>
            <HeroContent>
              <HeroTitle>Lawfully Intelligence</HeroTitle>
              <HeroTextContainer>
                <HeroSubtitle>
                  Real-time USCIS approval, RFE, and processing time
                  intelligence for H-1B, L-1, O-1, EB-2 NIW, EB-1A, and 18+
                  additional case types. 3-6 months ahead of USCIS.
                </HeroSubtitle>
                <CTAButton
                  href={intelligencePricingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Subscribe to Lawfully Intelligence
                </CTAButton>
                <PricingText>
                  Starting at $250/month (full organization)
                  <br />
                  Cancel anytime.
                </PricingText>
              </HeroTextContainer>
              <TrustBadges>
                <TrustBadge>
                  <TrustBadgeIcon1 />
                  <span>Real-time Updates</span>
                </TrustBadge>
                <TrustBadge>
                  <TrustBadgeIcon2 />
                  <span>Expert Analysis</span>
                </TrustBadge>
                <TrustBadge>
                  <TrustBadgeIcon3 />
                  <span>Predictive Intelligence</span>
                </TrustBadge>
              </TrustBadges>
            </HeroContent>
          </HeroSection>

          {/* Features Section */}
          <FeaturesSection>
            {/* Sub-section 1: What's included */}
            <FeaturesSubSection>
              <FeaturesHeader>
                <FeaturesTitle>
                  What&apos;s included with Lawfully Intelligence
                </FeaturesTitle>
                <FeaturesSubtitle>
                  Unlock the full spectrum of immigration strategy with
                  data-driven insights
                </FeaturesSubtitle>
              </FeaturesHeader>
              <FeaturesGrid>
                {INCLUDED_FEATURES.map((feature, index) => (
                  <FeatureCard key={index}>
                    <FeatureCardContent>
                      <FeatureIcon>{getFeatureIcon(feature.icon)}</FeatureIcon>
                      <FeatureTitle>{feature.title}</FeatureTitle>
                      <FeatureDescription>
                        {feature.description}
                      </FeatureDescription>
                    </FeatureCardContent>
                  </FeatureCard>
                ))}
              </FeaturesGrid>
            </FeaturesSubSection>

            {/* Sub-section 2: What it enables */}
            <FeaturesSubSection>
              <FeaturesHeader>
                <FeaturesTitle>
                  What Lawfully Intelligence enables for law firms and global
                  mobility teams
                </FeaturesTitle>
              </FeaturesHeader>
              <EnablesGrid>
                {ENABLES_FEATURES.map((feature, index) => (
                  <EnablesCard key={index}>
                    <FeatureCardContent>
                      <FeatureIcon>{getFeatureIcon(feature.icon)}</FeatureIcon>
                      <FeatureTitle>{feature.title}</FeatureTitle>
                      <FeatureDescription>
                        {feature.description}
                      </FeatureDescription>
                    </FeatureCardContent>
                  </EnablesCard>
                ))}
              </EnablesGrid>
            </FeaturesSubSection>
          </FeaturesSection>

          {/* Data Visualization Section */}
          <DataVisualizationSection>
            <DataVizHeader>
              <DataVizTitle>Precision Analytics</DataVizTitle>
              <DataVizSubtitle>
                Deep dive into USCIS trends with our data intelligence platform
              </DataVizSubtitle>
            </DataVizHeader>

            {/* H-1B Approval Rates Chart */}
            <ChartRow>
              <ChartPanel>
                <ChartPanelHeader>
                  <ChartTitleContainer>
                    <ChartIconWrapper color="#bef264">
                      <ChartTrendIcon color="#bef264" />
                    </ChartIconWrapper>
                    <ChartTitle>H-1B Approval Rates</ChartTitle>
                  </ChartTitleContainer>
                  <LiveDataBadge>
                    <LiveDataText>Live Data</LiveDataText>
                  </LiveDataBadge>
                </ChartPanelHeader>
                <ChartArea>
                  <ApprovalRatesChart />
                </ChartArea>
              </ChartPanel>
              <LegendPanel>
                <LegendItem>
                  <LegendDot color="#dc2626" />
                  <LegendLabel>USCIS Approval Rate</LegendLabel>
                </LegendItem>
                <LegendItem>
                  <LegendDot color="#bef264" />
                  <LegendLabel>Lawfully Approval Rate</LegendLabel>
                </LegendItem>
                <LegendNote>Lawfully Margin of Error = ±2%</LegendNote>
                <LegendNote>
                  Lawfully Sample: 66% regular; 34% premium
                </LegendNote>
              </LegendPanel>
            </ChartRow>

            {/* H-1B RFE Rates Chart */}
            <ChartRow>
              <ChartPanel>
                <ChartPanelHeader>
                  <ChartTitleContainer>
                    <ChartIconWrapper color="#d946ef">
                      <ChartTrendIcon color="#d946ef" />
                    </ChartIconWrapper>
                    <ChartTitle>H-1B RFE Rates</ChartTitle>
                  </ChartTitleContainer>
                  <LiveDataBadge>
                    <LiveDataText>Live Data</LiveDataText>
                  </LiveDataBadge>
                </ChartPanelHeader>
                <ChartArea>
                  <RFERatesChart />
                </ChartArea>
              </ChartPanel>
              <LegendPanel>
                <LegendItem>
                  <LegendDot color="#d946ef" />
                  <LegendLabel>Lawfully RFE Rate (regular)</LegendLabel>
                </LegendItem>
                <LegendItem>
                  <LegendDot color="#2563eb" />
                  <LegendLabel>Lawfully RFE Rate (premium)</LegendLabel>
                </LegendItem>
                <LegendNote>Lawfully Margin of Error = ±2%</LegendNote>
                <LegendNote>
                  Lawfully Sample: 66% regular; 34% premium
                </LegendNote>
              </LegendPanel>
            </ChartRow>

            {/* H-1B Processing Times Chart */}
            <ChartRow>
              <ChartPanel>
                <ChartPanelHeader>
                  <ChartTitleContainer>
                    <ChartIconWrapper color="#a03cff">
                      <ChartTrendIcon color="#a03cff" />
                    </ChartIconWrapper>
                    <ChartTitle>H-1B Processing Times</ChartTitle>
                  </ChartTitleContainer>
                  <LiveDataBadge>
                    <LiveDataText>Live Data</LiveDataText>
                  </LiveDataBadge>
                </ChartPanelHeader>
                <ChartArea>
                  <ProcessingTimesChart />
                </ChartArea>
              </ChartPanel>
              <LegendPanel>
                <LegendItem>
                  <LegendDot color="#a03cff" />
                  <LegendLabel>Lawfully Processing Times (months)</LegendLabel>
                </LegendItem>
                <LegendNote>Lawfully Margin of Error = ±2%</LegendNote>
                <LegendNote>Lawfully Sample: regular processed only</LegendNote>
              </LegendPanel>
            </ChartRow>
          </DataVisualizationSection>

          {/* Experts Section */}
          <ExpertsSection>
            <ExpertsSectionContent>
              <SectionHeader>
                <SectionTitle>Analysis from Expert Contributors</SectionTitle>
                <SectionSubtitle>
                  Insights from former USCIS officials and immigration
                  specialists
                </SectionSubtitle>
              </SectionHeader>
              <ExpertsGrid>
                {EXPERTS.map((expert, index) => (
                  <ExpertCard key={index}>
                    <ExpertImageContainer>
                      <ExpertImageGlow />
                      <ExpertImage>
                        <img src={expert.image} alt={expert.name} />
                      </ExpertImage>
                      <VerifiedBadge>
                        <VerifiedCheckIcon />
                      </VerifiedBadge>
                    </ExpertImageContainer>
                    {expert.linkedin ? (
                      <ExpertName
                        as="a"
                        href={expert.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none", cursor: "pointer" }}
                      >
                        {expert.name}
                      </ExpertName>
                    ) : (
                      <ExpertName>{expert.name}</ExpertName>
                    )}
                    <ExpertTitle>{expert.title}</ExpertTitle>
                  </ExpertCard>
                ))}
              </ExpertsGrid>
            </ExpertsSectionContent>
          </ExpertsSection>

          {/* CTA Section */}
          <CTASection>
            <CTAContainer>
              <CTATitle>
                Ready to get ahead of USCIS processing trends?
              </CTATitle>
              <CTADescription>
                Join hundreds of immigration professionals and HR teams who rely
                on Lawfully Intelligence for strategic decision-making.
              </CTADescription>
              <CTAButton
                href={intelligencePricingUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Subscribe to Lawfully Intelligence
              </CTAButton>
            </CTAContainer>
          </CTASection>
        </ContentWrapper>
      </PageWrapper>
    </PageLayout>
  );
};

export default IntelligencePage;
